import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatXOF } from "@/lib/api";
import { Star, Search, Filter } from "lucide-react";

const ALL_SPECIALTIES = [
  "Écoute émotionnelle", "Soutien femmes", "Immigration", "Famille",
  "Jeunes", "Couples", "Motivation", "Orientation",
];
const ALL_LANGUAGES = ["Français", "Wolof", "Anglais"];

export default function Accompagnateurs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [language, setLanguage] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (specialty) params.specialty = specialty;
        if (language) params.language = language;
        const { data } = await api.get("/accompagnateurs", { params });
        setItems(data.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [specialty, language]);

  const filtered = items.filter((a) =>
    !q ||
    a.name.toLowerCase().includes(q.toLowerCase()) ||
    a.bio.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="bg-bone">
      <section className="container-jamm pt-14 pb-8">
        <div className="overline">Accompagnateurs</div>
        <h1 className="h-display mt-3">Choisissez la <span className="italic text-sage-600">bonne personne</span> pour vous.</h1>
        <p className="lead mt-4 max-w-2xl">Filtrez par spécialité ou par langue. Lisez les histoires. Faites confiance à votre intuition.</p>
      </section>

      <section className="container-jamm pb-8">
        <div className="card-soft p-5 md:p-6 flex flex-col md:flex-row gap-3 md:items-center">
          <div className="flex-1 flex items-center rounded-xl border border-border bg-white px-3">
            <Search className="w-4 h-4 text-ink-mute" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un nom, un mot…"
              className="flex-1 px-3 py-2.5 bg-transparent focus:outline-none"
              data-testid="acc-search"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-ink-mute" />
              <select value={specialty} onChange={(e) => setSpecialty(e.target.value)} className="rounded-xl border border-border bg-white px-3 py-2.5" data-testid="acc-specialty">
                <option value="">Toutes spécialités</option>
                {ALL_SPECIALTIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <select value={language} onChange={(e) => setLanguage(e.target.value)} className="rounded-xl border border-border bg-white px-3 py-2.5" data-testid="acc-language">
              <option value="">Toutes langues</option>
              {ALL_LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>
      </section>

      <section className="container-jamm pb-20">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <div key={i} className="card-soft animate-pulse h-72 bg-beige/40" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="card-soft text-center py-16 text-ink-soft" data-testid="acc-empty">Aucun accompagnateur ne correspond à votre recherche.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((a) => (
              <Link key={a.id} to={`/accompagnateurs/${a.id}`} className="card-soft card-soft-hover block" data-testid={`acc-list-card-${a.id}`}>
                <div className="flex gap-4">
                  <img src={a.avatar_url} alt={a.name} className="w-16 h-16 rounded-full object-cover ring-2 ring-sage-100" />
                  <div className="flex-1">
                    <h3 className="font-display text-xl text-ink">{a.name}</h3>
                    <div className="text-ink-soft text-sm">{a.title}</div>
                    <div className="flex items-center gap-1.5 text-sm mt-1">
                      <Star className="w-4 h-4 text-gold-dark" fill="currentColor" />
                      <span className="font-medium text-ink">{a.rating}</span>
                      <span className="text-ink-mute text-xs">· {a.consultations} séances</span>
                    </div>
                  </div>
                </div>
                <p className="text-ink-soft mt-4 text-sm leading-relaxed line-clamp-3">{a.bio}</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {a.specialties?.slice(0, 3).map((s) => <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-sage-50 text-sage-700 border border-sage-100">{s}</span>)}
                </div>
                <div className="mt-5 flex items-center justify-between">
                  <span className="text-xs text-ink-mute">à partir de</span>
                  <span className="font-display text-2xl text-sage-700">{formatXOF(a.rate_15)}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
